import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { FileUpload } from 'primereact/fileupload';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { ProgressBar } from 'primereact/progressbar';
import { Tag } from 'primereact/tag';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { ResearchProject, ProjectKoboContext, KoboAccount } from '../types';
import { apiService } from '../services/api';
import { useOptimizedWorkflowStatus } from '../hooks/useOptimizedWorkflowStatus';
import { useBackgroundProcess } from '../components/BackgroundProcessIndicator';
import WorkflowHistory from '../components/WorkflowHistory';
import WorkflowReviewPanel from '../components/WorkflowReviewPanel';
import WorkflowNotifications from '../components/WorkflowNotifications';

const ProjectWorkflowPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<ResearchProject | null>(null);
  const [koboContext, setKoboContext] = useState<ProjectKoboContext | null>(null);
  const [koboAccounts, setKoboAccounts] = useState<KoboAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [currentWorkflowArn, setCurrentWorkflowArn] = useState<string | null>(null);
  const [showTORBuilderDialog, setShowTORBuilderDialog] = useState(false);
  const [torBuilderResponse, setTorBuilderResponse] = useState<string>('');
  const [torBuilderProgress, setTorBuilderProgress] = useState<any>({});
  const [torBuilderCompleted, setTorBuilderCompleted] = useState(false);
  const [torBuilderLoading, setTorBuilderLoading] = useState(false);
  const [showReviewPanel, setShowReviewPanel] = useState(false);
  const toast = useRef<Toast>(null);

  // Background process management
  const { addProcess, updateProcess, completeProcess, failProcess } = useBackgroundProcess();

  // Workflow status hook with enhanced callbacks and caching
  const {
    workflow,
    loading: workflowLoading,
    error: workflowError,
    startPolling,
    isPolling,
    refresh: refreshWorkflow,
    pauseWorkflow,
    resumeWorkflow,
    cancelWorkflow,
    retryWorkflow
  } = useOptimizedWorkflowStatus({
    projectId: projectId || '',
    executionArn: currentWorkflowArn || undefined,
    autoStart: false,
    enableCaching: true,
    enableOptimisticUpdates: true,
    onStatusChange: (status, workflowData) => {
      console.log(`Workflow status changed to: ${status}`);
      // Update background process
      if (currentWorkflowArn) {
        updateProcess(currentWorkflowArn, {
          title: `${project?.title} Workflow`,
          description: `Current step: ${workflowData.current_step || 'Processing'}`,
          progress: getWorkflowProgress(status, workflowData.current_step),
          status: status === 'RUNNING' ? 'running' : 
                  status === 'SUCCEEDED' ? 'completed' :
                  status === 'FAILED' ? 'failed' : 'paused'
        });
      }
      
      if (status === 'RUNNING') {
        setProcessing(true);
      }
    },
    onComplete: (workflowData) => {
      console.log('Workflow completed:', workflowData);
      toast.current?.show({ 
        severity: 'success', 
        summary: 'Success', 
        detail: 'Workflow completed successfully!' 
      });
      setProcessing(false);
      loadProject(); // Reload project to get updated data
      
      // Complete background process
      if (currentWorkflowArn) {
        completeProcess(currentWorkflowArn);
      }
      
      setCurrentWorkflowArn(null);
    },
    onError: (error, workflowData) => {
      console.log('Workflow error:', error, workflowData);
      toast.current?.show({ 
        severity: 'error', 
        summary: 'Workflow Failed', 
        detail: error || 'Workflow execution failed' 
      });
      setProcessing(false);
      
      // Fail background process
      if (currentWorkflowArn) {
        failProcess(currentWorkflowArn, error);
      }
    }
  });

  const workflowSteps = [
    { label: 'Upload TOR', icon: 'pi pi-upload', color: '#3B82F6' },
    { label: 'Extract Indicators', icon: 'pi pi-search', color: '#22C55E' },
    { label: 'Design Questionnaire', icon: 'pi pi-list', color: '#F97316' },
    { label: 'Deploy to Kobo', icon: 'pi pi-send', color: '#A855F7' },
  ];

  useEffect(() => { 
    if (projectId) {
      loadProject();
      loadKoboContext();
      loadKoboAccounts();
    }
  }, [projectId]);
  
  useEffect(() => { 
    if (project) updateActiveStep(); 
  }, [project]);

  // Remove old useEffect handlers since they're now in the hook callbacks
  // useEffect(() => {
  //   if (workflow && workflow.status === 'SUCCEEDED') {
  //     toast.current?.show({ 
  //       severity: 'success', 
  //       summary: 'Success', 
  //       detail: 'Workflow completed successfully!' 
  //     });
  //     // Reload project to get updated data
  //     loadProject();
  //     setCurrentWorkflowArn(null);
  //   } else if (workflow && ['FAILED', 'TIMED_OUT', 'ABORTED'].includes(workflow.status)) {
  //     toast.current?.show({ 
  //       severity: 'error', 
  //       summary: 'Workflow Failed', 
  //       detail: workflow.error || 'Workflow execution failed' 
  //     });
  //   }
  // }, [workflow?.status]);

  // Handle workflow errors
  useEffect(() => {
    if (workflowError) {
      toast.current?.show({ 
        severity: 'error', 
        summary: 'Error', 
        detail: workflowError 
      });
    }
  }, [workflowError]);

  const loadProject = async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      const response = await apiService.getProject(projectId);
      if (response.success && response.data) setProject(response.data);
      else throw new Error(response.error || 'Project not found');
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load project' });
      navigate('/app/projects');
    } finally {
      setLoading(false);
    }
  };

  const loadKoboContext = async () => {
    if (!projectId) return;
    try {
      const response = await apiService.getProjectKoboContext(projectId);
      if (response.success && response.data) {
        setKoboContext(response.data);
      } else {
        // API returned error or no data - set empty context to stop loading state
        console.warn('KoboToolbox context not available:', response.error);
        setKoboContext({
          project_id: projectId || '',
          kobo_account_id: '',
          account_name: 'No KoboToolbox Account',
          server_url: '',
          deployment_status: 'not_deployed',
          account_active: false
        });
      }
    } catch (error) {
      console.error('Failed to load KoboToolbox context:', error);
      // Set empty context to stop loading state
      setKoboContext({
        project_id: projectId || '',
        kobo_account_id: '',
        account_name: 'No KoboToolbox Account',
        server_url: '',
        deployment_status: 'not_deployed',
        account_active: false
      });
    }
  };

  const loadKoboAccounts = async () => {
    try {
      const response = await apiService.getUserProfile();
      if (response.success && response.data) {
        setKoboAccounts(response.data.kobo_accounts || []);
      }
    } catch (error) {
      console.error('Failed to load KoboToolbox accounts:', error);
    }
  };

  const updateActiveStep = () => {
    if (!project) return;
    switch (project.status) {
      case 'draft': setActiveStep(0); break;
      case 'tor_uploaded': setActiveStep(1); break;
      case 'indicators_extracted': setActiveStep(2); break;
      case 'questionnaire_designed': setActiveStep(3); break;
      default: setActiveStep(4);
    }
  };

  const getWorkflowProgress = (status: string, currentStep?: string): number => {
    if (status === 'SUCCEEDED') return 100;
    if (status === 'FAILED' || status === 'TIMED_OUT' || status === 'ABORTED') return 0;
    
    // Calculate progress based on current step
    switch (currentStep) {
      case 'ExtractIndicators':
        return 33;
      case 'DesignQuestionnaire':
        return 66;
      case 'DeployToKobo':
        return 90;
      default:
        return 10;
    }
  };

  const handleTorUpload = async (event: any) => {
    if (!projectId || !event.files?.length) return;
    try {
      setProcessing(true);
      const response = await apiService.uploadTorDocument(projectId, event.files[0]);
      if (response.success) {
        toast.current?.show({ severity: 'success', summary: 'Success', detail: 'TOR uploaded' });
        await loadProject();
      }
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to upload TOR' });
    } finally {
      setProcessing(false);
    }
  };

  // Enhanced workflow method
  const handleStartFullWorkflow = async () => {
    if (!projectId) return;
    try {
      setProcessing(true);
      const response = await apiService.startFullWorkflow(projectId, {
        questionnaire_config: {
          title: `${project?.title} Survey`,
          description: project?.description,
          include_demographics: true,
          language: 'en'
        },
        deployment_config: {
          deploy_immediately: true
        }
      });
      
      if (response.success && response.data) {
        setCurrentWorkflowArn(response.data.execution_arn);
        
        // Add background process
        addProcess({
          id: response.data.execution_arn,
          title: `${project?.title} Workflow`,
          description: 'Starting full research workflow...',
          status: 'running',
          progress: 0,
          canCancel: true,
          canPause: true,
          onCancel: async () => {
            await cancelWorkflow();
          },
          onPause: async () => {
            await pauseWorkflow();
          },
          onResume: async () => {
            await resumeWorkflow();
          }
        });
        
        startPolling(response.data.execution_arn);
        toast.current?.show({ 
          severity: 'info', 
          summary: 'Workflow Started', 
          detail: 'Full workflow is now running. This may take several minutes.' 
        });
      } else {
        // Enhanced error messaging with specific API error
        const errorMessage = response.error || 'Failed to start workflow';
        throw new Error(errorMessage);
      }
    } catch (error) {
      // Enhanced error messaging with specific details
      const errorMessage = error instanceof Error ? error.message : 'Failed to start workflow';
      
      // Show specific error message to user
      toast.current?.show({ 
        severity: 'error', 
        summary: 'Workflow Failed to Start', 
        detail: errorMessage,
        life: 8000 // Show longer for important errors
      });
      
      // If it's a TOR-related error, highlight the TOR upload step
      if (errorMessage.toLowerCase().includes('tor') || errorMessage.toLowerCase().includes('document')) {
        // Scroll to TOR upload section
        const torSection = document.querySelector('[data-step="tor-upload"]');
        if (torSection) {
          torSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      
      setProcessing(false);
    }
  };

  // Continue workflow from current step for existing projects
  const handleContinueWorkflow = async () => {
    if (!projectId || !project) return;
    
    try {
      setProcessing(true);
      let workflowType = '';
      let description = '';
      
      // Determine which steps to run based on current project status
      if (project.status === 'indicators_extracted') {
        workflowType = 'questionnaire_and_kobo';
        description = 'Continuing from questionnaire design...';
      } else if (project.status === 'questionnaire_designed') {
        workflowType = 'kobo_deployment_only';
        description = 'Deploying to KoboToolbox...';
      }
      
      const response = await apiService.startWorkflow(projectId, {
        workflow_type: workflowType,
        questionnaire_config: {
          title: `${project.title} Survey`,
          description: project.description,
          include_demographics: true,
          language: 'en'
        },
        deployment_config: {
          deploy_immediately: true
        }
      });
      
      if (response.success && response.data) {
        setCurrentWorkflowArn(response.data.execution_arn);
        
        // Add background process
        addProcess({
          id: response.data.execution_arn,
          title: `${project.title} Workflow`,
          description,
          status: 'running',
          progress: project.status === 'indicators_extracted' ? 33 : 66,
          canCancel: true,
          canPause: true,
          onCancel: async () => {
            await cancelWorkflow();
          },
          onPause: async () => {
            await pauseWorkflow();
          },
          onResume: async () => {
            await resumeWorkflow();
          }
        });
        
        startPolling(response.data.execution_arn);
        toast.current?.show({ 
          severity: 'info', 
          summary: 'Workflow Continued', 
          detail: 'Continuing workflow from current step. This may take a few minutes.' 
        });
      } else {
        // Enhanced error messaging with specific API error
        const errorMessage = response.error || 'Failed to continue workflow';
        throw new Error(errorMessage);
      }
    } catch (error) {
      // Enhanced error messaging with specific details
      const errorMessage = error instanceof Error ? error.message : 'Failed to continue workflow';
      
      toast.current?.show({ 
        severity: 'error', 
        summary: 'Workflow Failed to Continue', 
        detail: errorMessage,
        life: 8000
      });
      
      // If it's a TOR-related error, highlight the TOR upload step
      if (errorMessage.toLowerCase().includes('tor') || errorMessage.toLowerCase().includes('document')) {
        const torSection = document.querySelector('[data-step="tor-upload"]');
        if (torSection) {
          torSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      
      setProcessing(false);
    }
  };

  // Re-run entire workflow for completed projects
  const handleRerunWorkflow = async () => {
    if (!projectId || !project) return;
    
    try {
      setProcessing(true);
      const response = await apiService.startWorkflow(projectId, {
        workflow_type: 'full_rerun',
        questionnaire_config: {
          title: `${project.title} Survey (Updated)`,
          description: project.description,
          include_demographics: true,
          language: 'en'
        },
        deployment_config: {
          deploy_immediately: true,
          update_existing: true
        }
      });
      
      if (response.success && response.data) {
        setCurrentWorkflowArn(response.data.execution_arn);
        
        // Add background process
        addProcess({
          id: response.data.execution_arn,
          title: `${project.title} Workflow (Re-run)`,
          description: 'Re-running entire research workflow...',
          status: 'running',
          progress: 0,
          canCancel: true,
          canPause: true,
          onCancel: async () => {
            await cancelWorkflow();
          },
          onPause: async () => {
            await pauseWorkflow();
          },
          onResume: async () => {
            await resumeWorkflow();
          }
        });
        
        startPolling(response.data.execution_arn);
        toast.current?.show({ 
          severity: 'info', 
          summary: 'Workflow Re-started', 
          detail: 'Re-running entire workflow with updated settings. This may take several minutes.' 
        });
      } else {
        // Enhanced error messaging with specific API error
        const errorMessage = response.error || 'Failed to re-run workflow';
        throw new Error(errorMessage);
      }
    } catch (error) {
      // Enhanced error messaging with specific details
      const errorMessage = error instanceof Error ? error.message : 'Failed to re-run workflow';
      
      toast.current?.show({ 
        severity: 'error', 
        summary: 'Workflow Failed to Re-run', 
        detail: errorMessage,
        life: 8000
      });
      
      // If it's a TOR-related error, highlight the TOR upload step
      if (errorMessage.toLowerCase().includes('tor') || errorMessage.toLowerCase().includes('document')) {
        const torSection = document.querySelector('[data-step="tor-upload"]');
        if (torSection) {
          torSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      
      setProcessing(false);
    }
  };

  // Resume workflow from last checkpoint (idempotent)
  const handleResumeWorkflow = async () => {
    if (!projectId || !project) return;
    
    try {
      setProcessing(true);
      const response = await apiService.resumeWorkflowFromCheckpoint(projectId);
      
      if (response.success && response.data) {
        const { step, message, result, next_step, completed } = response.data;
        
        // Show what was done
        let detailMessage = message;
        if (result.skipped) {
          detailMessage += ' (Step was already completed, skipped)';
        }
        
        toast.current?.show({ 
          severity: 'success', 
          summary: `Step: ${step}`, 
          detail: detailMessage,
          life: 6000
        });
        
        // Reload project to get updated data
        await loadProject();
        
        // If there's a next step, show it
        if (next_step && !completed) {
          toast.current?.show({ 
            severity: 'info', 
            summary: 'Next Step Available', 
            detail: `You can now proceed to: ${next_step}`,
            life: 5000
          });
        } else if (completed) {
          toast.current?.show({ 
            severity: 'success', 
            summary: 'Workflow Complete!', 
            detail: 'All workflow steps have been completed successfully.',
            life: 5000
          });
        }
      } else {
        throw new Error(response.error || 'Failed to resume workflow');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to resume workflow';
      
      toast.current?.show({ 
        severity: 'error', 
        summary: 'Resume Failed', 
        detail: errorMessage,
        life: 8000
      });
    } finally {
      setProcessing(false);
    }
  };

  // Enhanced methods for individual steps
  const handleExtractIndicators = async () => {
    if (!projectId) return;
    try {
      setProcessing(true);
      
      const response = await apiService.startIndicatorExtraction(projectId);
      
      if (response.success && response.data) {
        setCurrentWorkflowArn(response.data.execution_arn);
        startPolling(response.data.execution_arn);
        toast.current?.show({ 
          severity: 'info', 
          summary: 'Processing', 
          detail: 'Extracting indicators. This may take a few minutes.' 
        });
      } else {
        // Enhanced error messaging with specific API error
        const errorMessage = response.error || 'Failed to start indicator extraction';
        throw new Error(errorMessage);
      }
    } catch (error) {
      // Enhanced error messaging with specific details
      const errorMessage = error instanceof Error ? error.message : 'Failed to extract indicators';
      
      toast.current?.show({ 
        severity: 'error', 
        summary: 'Indicator Extraction Failed', 
        detail: errorMessage,
        life: 8000
      });
      
      // If it's a TOR-related error, highlight the TOR upload step
      if (errorMessage.toLowerCase().includes('tor') || errorMessage.toLowerCase().includes('document')) {
        const torSection = document.querySelector('[data-step="tor-upload"]');
        if (torSection) {
          torSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      
      setProcessing(false);
    }
  };

  const handleDesignQuestionnaire = async () => {
    if (!projectId) return;
    try {
      setProcessing(true);
      
      const response = await apiService.startQuestionnaireDesign(projectId, {
        title: `${project?.title} Survey`,
        description: project?.description,
        include_demographics: true,
        language: 'en'
      });
      
      if (response.success && response.data) {
        setCurrentWorkflowArn(response.data.execution_arn);
        startPolling(response.data.execution_arn);
        toast.current?.show({ 
          severity: 'info', 
          summary: 'Processing', 
          detail: 'Designing questionnaire. This may take a few minutes.' 
        });
      } else {
        throw new Error(response.error || 'Failed to start questionnaire design');
      }
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to design questionnaire' });
      setProcessing(false);
    }
  };

  const handleChangeKoboAccount = async (newAccountId: string) => {
    if (!projectId) return;
    try {
      setProcessing(true);
      const response = await apiService.updateProjectKoboAccount(projectId, newAccountId);
      if (response.success) {
        toast.current?.show({ severity: 'success', summary: 'Success', detail: 'KoboToolbox account updated' });
        await loadProject();
        await loadKoboContext();
      } else {
        throw new Error(response.error || 'Failed to update KoboToolbox account');
      }
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to update KoboToolbox account' });
    } finally {
      setProcessing(false);
    }
  };

  const handleDeployToKobo = async () => {
    if (!projectId) return;
    
    // Check if KoboToolbox account is active
    if (koboContext && !koboContext.account_active) {
      toast.current?.show({ 
        severity: 'warn', 
        summary: 'Account Inactive', 
        detail: 'The associated KoboToolbox account is inactive. Please check your account settings.' 
      });
      return;
    }
    
    try {
      setProcessing(true);
      
      const response = await apiService.startKoboDeployment(projectId, {
        form_name: `${project?.title} Survey`,
        deploy_immediately: true
      });
      
      if (response.success && response.data) {
        setCurrentWorkflowArn(response.data.execution_arn);
        startPolling(response.data.execution_arn);
        toast.current?.show({ 
          severity: 'info', 
          summary: 'Processing', 
          detail: 'Deploying to KoboToolbox. This may take a few minutes.' 
        });
      } else {
        throw new Error(response.error || 'Failed to start KoboToolbox deployment');
      }
    } catch (error) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to deploy' });
      setProcessing(false);
    }
  };

  const handleContactSupport = (errorDetails: string) => {
    // Create a mailto link with pre-filled error details
    const subject = encodeURIComponent(`Workflow Error - Project: ${project?.title}`);
    const body = encodeURIComponent(`
Hello Support Team,

I encountered an error while running a workflow in my project. Please find the details below:

Project Information:
- Project ID: ${projectId}
- Project Title: ${project?.title}
- Project Description: ${project?.description}

${errorDetails}

Please help me resolve this issue.

Thank you,
${localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')!).name : 'User'}
    `);
    
    const mailtoLink = `mailto:support@insights.ps?subject=${subject}&body=${body}`;
    window.open(mailtoLink);
    
    toast.current?.show({
      severity: 'info',
      summary: 'Support Contact',
      detail: 'Your email client should open with pre-filled error details. If not, please copy the error details and send them to support@insights.ps'
    });
  };

  // Manual KoboToolbox deployment from review panel
  const handleManualKoboDeployment = async (questionnaire: any) => {
    if (!projectId) return;
    
    try {
      setProcessing(true);
      
      // Use the async KoboToolbox deployment workflow
      console.log('Deploying questionnaire:', questionnaire);
      
      // Call the async KoboToolbox deployment API
      const response = await apiService.startKoboDeployment(projectId, {
        form_name: `${project?.title} Survey`,
        deploy_immediately: true
      });
      
      if (response.success && response.data) {
        setCurrentWorkflowArn(response.data.execution_arn);
        startPolling(response.data.execution_arn);
        toast.current?.show({ 
          severity: 'info', 
          summary: 'Processing', 
          detail: 'Deploying questionnaire to KoboToolbox. This may take a few minutes.' 
        });
      } else {
        throw new Error(response.error || 'Failed to deploy to KoboToolbox');
      }
    } catch (error) {
      console.error('Manual deployment error:', error);
      throw error; // Re-throw to be handled by the review panel
    } finally {
      setProcessing(false);
    }
  };

  // TOR Builder Agent Integration
  const handleOpenTORBuilder = async () => {
    try {
      setProcessing(true);
      
      // Call the TOR Builder Agent
      const response = await apiService.invokeTORBuilder({
        prompt: `I want to create a TOR for my research project: ${project?.title}. ${project?.description ? `Description: ${project.description}` : ''}`,
        project_id: projectId
      });
      
      // The API returns the response directly with success, response, and tor_progress fields
      if (response.success && response.data) {
        // Open TOR Builder in a dialog
        setShowTORBuilderDialog(true);
        setTorBuilderResponse(response.data.response);
        setTorBuilderProgress(response.data.tor_progress || {});
        setTorBuilderCompleted(false);
      } else {
        throw new Error(response.error || 'Failed to start TOR Builder');
      }
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'TOR Builder Error',
        detail: 'Failed to start TOR Builder. Please try again.',
        life: 5000
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleTORBuilderMessage = async (message: string) => {
    try {
      setTorBuilderLoading(true);
      console.log('Sending message to TOR Builder:', message);
      const response = await apiService.invokeTORBuilder({
        prompt: message,
        project_id: projectId
      });
      
      console.log('TOR Builder response:', response);
      console.log('Response data:', response.data);
      console.log('TOR progress:', response.data?.tor_progress);
      
      if (response.success && response.data) {
        console.log('Setting TOR Builder response:', response.data.response);
        setTorBuilderResponse(response.data.response);
        
        const progressData = response.data.tor_progress || {};
        console.log('Setting progress data:', progressData);
        setTorBuilderProgress(progressData);
        
        // Force a re-render by logging the current progress state
        console.log('Current progress state after update:', progressData);
        
        // If TOR is completed, offer to save it
        if (response.data.tor_progress?.completion_percentage === 100) {
          console.log('TOR Builder completed!');
          setTorBuilderCompleted(true);
        }
      } else {
        console.error('TOR Builder API error:', response.error);
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: response.error || 'Failed to process your message. Please try again.',
          life: 5000
        });
      }
    } catch (error) {
      console.error('TOR Builder message error:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to process your message. Please try again.',
        life: 5000
      });
    } finally {
      setTorBuilderLoading(false);
    }
  };

  const cardStyle: React.CSSProperties = { backgroundColor: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '1rem' };

  // Helper function to generate sample TOR template
  const generateSampleTOR = (projectTitle?: string, projectDescription?: string): string => {
    const title = projectTitle || 'Research Project';
    const description = projectDescription || 'Research study to investigate key factors and outcomes';
    
    return `TERMS OF REFERENCE (TOR)
${title}

1. BACKGROUND AND CONTEXT
${description}

This research project aims to provide evidence-based insights to inform policy decisions and improve outcomes for the target population.

2. RESEARCH OBJECTIVES
Primary Objective:
- To assess and analyze key factors affecting [specify main topic area]

Secondary Objectives:
- To identify barriers and challenges in [relevant area]
- To measure the effectiveness of current interventions or services
- To provide actionable recommendations for policy and program improvements

3. RESEARCH QUESTIONS
Key Research Questions:
- What are the main factors influencing [primary outcome]?
- How do different population groups experience [relevant services/conditions]?
- What are the most effective strategies for improving [target outcomes]?
- What barriers prevent optimal outcomes and how can they be addressed?

4. TARGET POPULATION
Primary Target Population: [Specify main group to be studied]
Secondary Stakeholders: [List other relevant groups]
Geographic Scope: [Specify location/region]
Sample Size: [Estimated number of participants]

5. METHODOLOGY
Research Design: Mixed-methods approach combining quantitative and qualitative data collection
Data Collection Methods:
- Structured surveys/questionnaires
- Key informant interviews
- Focus group discussions
- Document review and analysis

6. EXPECTED OUTCOMES AND DELIVERABLES
- Comprehensive research report with findings and analysis
- Policy recommendations based on evidence
- Data visualizations and infographics
- Presentation of key findings to stakeholders
- Actionable implementation guidelines

7. KEY PERFORMANCE INDICATORS
Measurable Outcomes:
- [Specific quantitative indicators to be measured]
- [Quality indicators and satisfaction measures]
- [Access and utilization metrics]
- [Impact and effectiveness measures]

8. TIMELINE
Phase 1: Research design and instrument development (2-3 weeks)
Phase 2: Data collection and fieldwork (4-6 weeks)
Phase 3: Data analysis and report writing (3-4 weeks)
Phase 4: Stakeholder engagement and dissemination (1-2 weeks)

9. EXPECTED IMPACT
This research will contribute to:
- Evidence-based policy development
- Improved service delivery and outcomes
- Better understanding of target population needs
- Enhanced program effectiveness and efficiency

10. QUALITY ASSURANCE
- Rigorous methodology and data validation
- Stakeholder consultation throughout the process
- Peer review of findings and recommendations
- Ethical considerations and participant protection

---
INSTRUCTIONS FOR USE:
1. Customize the sections above with your specific research context
2. Replace bracketed placeholders with actual details
3. Add or remove sections as needed for your project
4. Ensure all objectives are measurable and achievable
5. Save as a document file and upload to the system

This template provides a foundation for creating a comprehensive TOR that will work well with the AI-powered research automation system.`;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#3B82F6' }}></i>
      </div>
    );
  }

  if (!project) {
    return (
      <div style={cardStyle}>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <h2 style={{ color: '#1f2937' }}>Project not found</h2>
          <Button label="Back to Projects" onClick={() => navigate('/app/projects')} />
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Workflow Notifications */}
      <WorkflowNotifications workflow={workflow} projectTitle={project?.title} />

      {/* Header Section */}
      <div style={{ padding: '2rem', backgroundColor: 'white', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ fontSize: '1.5rem', margin: '0 0 0.5rem 0', color: '#1f2937' }}>{project.title}</h1>
            <p style={{ color: '#6b7280', margin: 0 }}>{project.description}</p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button 
              label="Edit Project" 
              icon="pi pi-pencil" 
              text 
              onClick={() => navigate(`/app/projects/${projectId}/edit`)} 
            />
            <Button 
              label="Back to Projects" 
              icon="pi pi-arrow-left" 
              text 
              onClick={() => navigate('/app/projects')} 
            />
          </div>
        </div>
      </div>

      {/* KoboToolbox Account Section */}
      <div style={{ padding: '2rem', backgroundColor: '#f8f9fa' }}>
        <div style={cardStyle}>
          <h3 style={{ fontSize: '1.125rem', margin: '0 0 1rem 0', color: '#1f2937' }}>KoboToolbox Account</h3>
          {koboContext ? (
            koboContext.kobo_account_id ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', alignItems: 'center' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <i className="pi pi-cloud" style={{ color: koboContext.account_active ? '#22C55E' : '#EF4444' }}></i>
                    <span style={{ fontWeight: 500 }}>{koboContext.account_name}</span>
                    <Tag 
                      value={koboContext.account_active ? 'Active' : 'Inactive'} 
                      severity={koboContext.account_active ? 'success' : 'danger'} 
                    />
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    Server: {koboContext.server_url}
                  </div>
                  {koboContext.deployment_status !== 'not_deployed' && (
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                      Status: {koboContext.deployment_status}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  {koboAccounts.length > 0 && (
                    <Dropdown
                      value={project?.koboAccountId}
                      options={koboAccounts.map(account => ({
                        label: account.account_name,
                        value: account.account_id
                      }))}
                      onChange={(e) => handleChangeKoboAccount(e.value)}
                      placeholder="Change KoboToolbox account"
                      disabled={processing}
                    />
                  )}
                </div>
              </div>
            ) : (
              <div style={{ 
                padding: '1rem', 
                backgroundColor: '#fef3c7', 
                border: '1px solid #f59e0b', 
                borderRadius: '6px' 
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <i className="pi pi-exclamation-triangle" style={{ color: '#d97706' }}></i>
                  <span style={{ fontWeight: 500, color: '#92400e' }}>No KoboToolbox Account Configured</span>
                </div>
                <p style={{ fontSize: '0.875rem', color: '#a16207', margin: '0 0 0.75rem 0' }}>
                  You need to configure a KoboToolbox account to deploy surveys. Please add your KoboToolbox credentials in your profile settings.
                </p>
                <Button
                  label="Configure KoboToolbox Account"
                  icon="pi pi-cog"
                  size="small"
                  severity="warning"
                  onClick={() => navigate('/app/profile')}
                />
              </div>
            )
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <i className="pi pi-spin pi-spinner" style={{ color: '#3B82F6' }}></i>
              <span style={{ color: '#6b7280' }}>Loading KoboToolbox account information...</span>
            </div>
          )}
        </div>
      </div>

      {/* Workflow Progress Section - Removed blocking dialog, now inline only */}

      {/* Workflow Review Panel - Show when requested */}
      {showReviewPanel && (
        <div style={{ padding: '2rem', backgroundColor: '#f8f9fa' }}>
          <WorkflowReviewPanel
            workflow={workflow}
            projectId={projectId}
            project={project}
            onDeployToKobo={handleManualKoboDeployment}
            onRefresh={refreshWorkflow}
          />
        </div>
      )}

      {/* Workflow Steps Section */}
      <div style={{ padding: '2rem', backgroundColor: '#f8f9fa' }}>
        <div style={cardStyle}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '1rem' 
          }}>
            <h3 style={{ fontSize: '1.125rem', margin: 0, color: '#1f2937' }}>
              Research Workflow
            </h3>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {/* Debug info for project status */}
              <Tag 
                value={`Status: ${project?.status}`} 
                severity="info" 
                style={{ fontSize: '0.625rem' }}
              />
              
              {/* Show workflow options based on project status */}
              {project?.status === 'tor_uploaded' && (
                <Button
                  label="Run Full Workflow"
                  icon="pi pi-play"
                  onClick={handleStartFullWorkflow}
                  loading={processing}
                  severity="success"
                  size="small"
                />
              )}
              
              {/* Allow existing projects to run remaining workflow steps */}
              {(project?.status === 'indicators_extracted' || project?.status === 'questionnaire_designed') && (
                <Button
                  label="Continue Workflow"
                  icon="pi pi-play"
                  onClick={handleContinueWorkflow}
                  loading={processing}
                  severity="success"
                  size="small"
                  tooltip="Continue from current step using workflow"
                />
              )}
              
              {/* Allow re-running full workflow for completed projects */}
              {project?.status === 'kobo_deployed' && (
                <Button
                  label="Re-run Workflow"
                  icon="pi pi-refresh"
                  onClick={handleRerunWorkflow}
                  loading={processing}
                  severity="info"
                  size="small"
                  tooltip="Re-run the entire workflow with updated settings"
                />
              )}
              
              {/* Enhanced workflow buttons for ANY project with TOR uploaded or beyond */}
              {project?.status && project.status !== 'draft' && (
                <>
                  <Button
                    label="Start Workflow"
                    icon="pi pi-play-circle"
                    onClick={handleStartFullWorkflow}
                    loading={processing}
                    severity="success"
                    size="small"
                    tooltip="Start or restart the full workflow from current state"
                  />
                  
                  {/* Show continue button for projects that have some progress */}
                  {(project.indicators?.length || project.questionnaire || project.koboFormId) && (
                    <Button
                      label="Continue/Resume"
                      icon="pi pi-step-forward"
                      onClick={handleContinueWorkflow}
                      loading={processing}
                      severity="info"
                      size="small"
                      tooltip="Continue workflow from where it left off"
                    />
                  )}
                  
                  {/* Review Results button - show when workflow has run or is running */}
                  {(workflow || project.status === 'indicators_extracted' || project.status === 'questionnaire_designed' || project.status === 'kobo_deployed') && (
                    <Button
                      label={showReviewPanel ? "Hide Review" : "Review Results"}
                      icon={showReviewPanel ? "pi pi-eye-slash" : "pi pi-eye"}
                      onClick={() => setShowReviewPanel(!showReviewPanel)}
                      severity="info"
                      size="small"
                      outlined
                      tooltip="Review step-by-step workflow results and manually approve KoboToolbox deployment"
                    />
                  )}
                </>
              )}
            </div>
          </div>
          
          {/* Quick Action Section for Existing Projects */}
          {project?.status && project.status !== 'draft' && !processing && !isPolling && (
            <div style={{
              padding: '1.5rem',
              backgroundColor: '#f0f9ff',
              border: '2px solid #0ea5e9',
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '1rem'
              }}>
                <i className="pi pi-bolt" style={{ color: '#0ea5e9', fontSize: '1.5rem' }}></i>
                <div>
                  <h4 style={{
                    fontSize: '1rem',
                    color: '#0c4a6e',
                    margin: '0 0 0.25rem 0',
                    fontWeight: 600
                  }}>
                    Ready to Use Workflow System!
                  </h4>
                  <p style={{
                    fontSize: '0.875rem',
                    color: '#075985',
                    margin: 0,
                    lineHeight: 1.4
                  }}>
                    Your project has been upgraded to support the new workflow system. 
                    Choose an action below to get started:
                  </p>
                </div>
              </div>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '0.75rem'
              }}>
                <Button
                  label="🚀 Start Full Workflow"
                  onClick={handleStartFullWorkflow}
                  loading={processing}
                  severity="success"
                  style={{ justifyContent: 'flex-start' }}
                  tooltip="Run the complete workflow from start to finish"
                />
                
                {/* Resume button - shows for incomplete workflows */}
                {project?.status && !['kobo_deployed', 'draft'].includes(project.status) && (
                  <Button
                    label="▶️ Resume Workflow"
                    onClick={handleResumeWorkflow}
                    loading={processing}
                    severity="help"
                    style={{ justifyContent: 'flex-start' }}
                    tooltip="Resume workflow from last checkpoint (skips completed steps)"
                  />
                )}
                
                {(project.indicators?.length || project.questionnaire || project.koboFormId) && (
                  <Button
                    label="⏭️ Continue Workflow"
                    onClick={handleContinueWorkflow}
                    loading={processing}
                    severity="info"
                    style={{ justifyContent: 'flex-start' }}
                    tooltip="Continue from where the project left off"
                  />
                )}
                
                {project?.status === 'kobo_deployed' && (
                  <Button
                    label="🔄 Re-run Workflow"
                    onClick={handleRerunWorkflow}
                    loading={processing}
                    severity="warning"
                    style={{ justifyContent: 'flex-start' }}
                    tooltip="Re-run the entire workflow with updated settings"
                  />
                )}
              </div>
            </div>
          )}

          {/* TOR Upload Required Message for Draft Projects */}
          {project?.status === 'draft' && (
            <div style={{
              padding: '1.5rem',
              backgroundColor: '#fef3c7',
              border: '2px solid #f59e0b',
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '1rem'
              }}>
                <i className="pi pi-exclamation-triangle" style={{ color: '#d97706', fontSize: '1.5rem' }}></i>
                <div>
                  <h4 style={{
                    fontSize: '1rem',
                    color: '#92400e',
                    margin: '0 0 0.25rem 0',
                    fontWeight: 600
                  }}>
                    TOR Document Required
                  </h4>
                  <p style={{
                    fontSize: '0.875rem',
                    color: '#a16207',
                    margin: 0,
                    lineHeight: 1.4
                  }}>
                    Please upload your Terms of Reference document first. This is required to start the workflow 
                    and extract measurable indicators for your research project.
                  </p>
                </div>
              </div>
              
              <Button
                label="📄 Upload TOR Document"
                icon="pi pi-upload"
                onClick={() => {
                  const torSection = document.querySelector('[data-step="tor-upload"]');
                  if (torSection) {
                    torSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                }}
                severity="warning"
                style={{ justifyContent: 'flex-start' }}
              />
              <Button
                label="🤖 Create TOR with AI Assistant"
                icon="pi pi-android"
                onClick={handleOpenTORBuilder}
                loading={processing}
                severity="info"
                style={{ justifyContent: 'flex-start' }}
                tooltip="Interactive AI assistant guides you through creating a professional TOR"
              />
            </div>
          )}
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: processing || isPolling ? '1.5rem' : 0 }}>
            {workflowSteps.map((step, i) => (
              <div key={i} style={{ textAlign: 'center', opacity: i <= activeStep ? 1 : 0.5 }}>
                <div style={{ width: '3rem', height: '3rem', borderRadius: '50%', backgroundColor: i < activeStep ? '#22C55E' : i === activeStep ? step.color : '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 0.75rem auto' }}>
                  <i className={i < activeStep ? 'pi pi-check' : step.icon} style={{ color: 'white', fontSize: '1.25rem' }}></i>
                </div>
                <div style={{ fontSize: '0.875rem', fontWeight: 500, color: i <= activeStep ? '#1f2937' : '#9ca3af' }}>{step.label}</div>
              </div>
            ))}
          </div>
          {(processing || isPolling) && (
            <div>
              <ProgressBar mode="indeterminate" style={{ height: '6px' }} />
              <p style={{ textAlign: 'center', color: '#6b7280', marginTop: '0.5rem', marginBottom: 0 }}>
                {isPolling ? 'Workflow running...' : 'Processing...'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Steps Content Section */}
      <div style={{ padding: '2rem', backgroundColor: 'white' }}>
        {/* Step 1: Upload TOR */}
        <div style={cardStyle} data-step="tor-upload">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <div style={{
              width: '2rem',
              height: '2rem',
              borderRadius: '50%',
              backgroundColor: project.status === 'draft' ? '#3B82F6' : '#22C55E',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <i className={project.status === 'draft' ? 'pi pi-upload' : 'pi pi-check'} style={{ color: 'white', fontSize: '0.875rem' }}></i>
            </div>
            <h3 style={{ fontSize: '1.125rem', margin: 0, color: '#1f2937' }}>1. Upload Terms of Reference</h3>
            {project.status === 'draft' && (
              <span style={{
                backgroundColor: '#fef3c7',
                color: '#92400e',
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontWeight: 500
              }}>
                Required First Step
              </span>
            )}
          </div>
          
          {project.status === 'draft' ? (
            <div>
              <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
                <strong>Start here:</strong> Upload your Terms of Reference document (PDF, Word, or text file). 
                This document will be analyzed to extract measurable indicators for your research project.
              </p>
              
              {/* Enhanced TOR upload requirements */}
              <div style={{
                padding: '1rem',
                backgroundColor: '#f0f9ff',
                border: '1px solid #0ea5e9',
                borderRadius: '6px',
                marginBottom: '1rem'
              }}>
                <h4 style={{ fontSize: '0.875rem', color: '#0c4a6e', margin: '0 0 0.5rem 0' }}>
                  📋 TOR Document Requirements:
                </h4>
                <ul style={{ fontSize: '0.75rem', color: '#075985', margin: '0 0 0.75rem 0', paddingLeft: '1.25rem' }}>
                  <li>Accepted formats: PDF, Word (.doc/.docx), or plain text (.txt)</li>
                  <li>Maximum file size: 10MB</li>
                  <li>Should contain research objectives, questions, and methodology</li>
                  <li>Clear description of expected outcomes and deliverables</li>
                </ul>
                
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <Button
                    label="📖 View Sample Projects"
                    size="small"
                    text
                    onClick={() => window.open('/docs/sample-projects.md', '_blank')}
                    tooltip="See example project descriptions and TOR templates"
                  />
                  <Button
                    label="💡 TOR Writing Tips"
                    size="small"
                    text
                    onClick={() => {
                      toast.current?.show({
                        severity: 'info',
                        summary: 'TOR Writing Tips',
                        detail: 'A good TOR should include: 1) Clear research objectives, 2) Target population, 3) Key questions to answer, 4) Expected measurable outcomes, 5) Policy relevance. See sample projects for examples.',
                        life: 10000
                      });
                    }}
                    tooltip="Quick tips for writing effective TOR documents"
                  />
                  <Button
                    label="📝 Generate Sample TOR"
                    size="small"
                    text
                    onClick={() => {
                      const sampleTOR = generateSampleTOR(project?.title, project?.description);
                      navigator.clipboard.writeText(sampleTOR).then(() => {
                        toast.current?.show({
                          severity: 'success',
                          summary: 'Sample TOR Copied',
                          detail: 'A sample TOR template has been copied to your clipboard. You can paste it into a document editor, customize it, and then upload it.',
                          life: 8000
                        });
                      }).catch(() => {
                        // Fallback: show the sample TOR in a dialog or new window
                        const newWindow = window.open('', '_blank');
                        if (newWindow) {
                          newWindow.document.write(`<pre style="font-family: Arial; padding: 20px; line-height: 1.6;">${sampleTOR}</pre>`);
                          newWindow.document.title = 'Sample TOR Template';
                        }
                      });
                    }}
                    tooltip="Generate a sample TOR template based on your project"
                  />
                  <Button
                    label="🤖 TOR Builder Assistant"
                    size="small"
                    severity="help"
                    onClick={handleOpenTORBuilder}
                    loading={processing}
                    tooltip="Interactive AI assistant to help you create a TOR step-by-step"
                  />
                </div>
              </div>
              
              <FileUpload 
                mode="basic" 
                name="torDocument" 
                accept=".pdf,.doc,.docx,.txt" 
                maxFileSize={10000000} 
                onUpload={handleTorUpload} 
                auto 
                chooseLabel="📄 Choose TOR Document" 
                disabled={processing}
                style={{ width: '100%' }}
              />
              
              {/* Workflow preview */}
              <div style={{
                marginTop: '1rem',
                padding: '0.75rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '6px',
                fontSize: '0.75rem',
                color: '#6b7280'
              }}>
                <strong>Next steps after upload:</strong> Extract indicators → Design questionnaire → Deploy to KoboToolbox
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Tag value="✅ Uploaded" severity="success" />
              <span style={{ color: '#6b7280' }}>TOR document has been uploaded and processed</span>
              <Button
                label="Replace TOR"
                icon="pi pi-upload"
                size="small"
                text
                onClick={() => {
                  // Allow re-uploading TOR
                  const fileInput = document.createElement('input');
                  fileInput.type = 'file';
                  fileInput.accept = '.pdf,.doc,.docx,.txt';
                  fileInput.onchange = (e) => {
                    const files = (e.target as HTMLInputElement).files;
                    if (files && files.length > 0) {
                      handleTorUpload({ files: [files[0]] });
                    }
                  };
                  fileInput.click();
                }}
                tooltip="Upload a new TOR document to replace the current one"
              />
            </div>
          )}
        </div>

        {/* Step 2: Extract Indicators */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <div style={{
              width: '2rem',
              height: '2rem',
              borderRadius: '50%',
              backgroundColor: project.indicators?.length ? '#22C55E' : project.status === 'tor_uploaded' ? '#3B82F6' : '#e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <i className={project.indicators?.length ? 'pi pi-check' : 'pi pi-search'} style={{ color: 'white', fontSize: '0.875rem' }}></i>
            </div>
            <h3 style={{ fontSize: '1.125rem', margin: 0, color: '#1f2937' }}>2. Extract Indicators</h3>
            {project.indicators?.length && (
              <Tag value="Completed" severity="success" style={{ fontSize: '0.75rem' }} />
            )}
          </div>
          <p style={{ color: '#6b7280', marginBottom: '1rem' }}>AI will analyze your TOR and extract measurable indicators</p>
          {project.status === 'tor_uploaded' ? (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <Button 
                label="Extract Indicators" 
                icon="pi pi-search" 
                onClick={handleExtractIndicators} 
                loading={processing || isPolling} 
              />
            </div>
          ) : project.indicators?.length ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <Tag value="Extracted" severity="success" />
                <span style={{ color: '#6b7280' }}>{project.indicators.length} indicators extracted</span>
                {/* Allow re-extraction for existing projects */}
                <Button 
                  label="Re-extract" 
                  icon="pi pi-refresh" 
                  size="small"
                  severity="info"
                  onClick={handleExtractIndicators} 
                  loading={processing || isPolling}
                  tooltip="Re-run indicator extraction with workflow"
                />
              </div>
              <DataTable value={project.indicators} className="p-datatable-sm">
                <Column field="name" header="Indicator Name" />
                <Column field="definition" header="Definition" />
                <Column field="measurementType" header="Type" />
              </DataTable>
            </div>
          ) : (
            <span style={{ color: '#9ca3af' }}>Complete the TOR upload step first</span>
          )}
        </div>

        {/* Step 3: Design Questionnaire */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <div style={{
              width: '2rem',
              height: '2rem',
              borderRadius: '50%',
              backgroundColor: project.questionnaire ? '#22C55E' : project.status === 'indicators_extracted' ? '#3B82F6' : '#e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <i className={project.questionnaire ? 'pi pi-check' : 'pi pi-list'} style={{ color: 'white', fontSize: '0.875rem' }}></i>
            </div>
            <h3 style={{ fontSize: '1.125rem', margin: 0, color: '#1f2937' }}>3. Design Questionnaire</h3>
            {project.questionnaire && (
              <Tag value="Completed" severity="success" style={{ fontSize: '0.75rem' }} />
            )}
          </div>
          <p style={{ color: '#6b7280', marginBottom: '1rem' }}>Transform indicators into structured questionnaire items</p>
          {project.status === 'indicators_extracted' ? (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <Button 
                label="Design Questionnaire" 
                icon="pi pi-list" 
                onClick={handleDesignQuestionnaire} 
                loading={processing || isPolling} 
              />
            </div>
          ) : project.questionnaire ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <Tag value="Designed" severity="success" />
                <span style={{ color: '#6b7280' }}>{project.questionnaire.sections.reduce((t: number, s: any) => t + s.questions.length, 0)} questions created</span>
                {/* Allow re-design for existing projects */}
                <Button 
                  label="Re-design" 
                  icon="pi pi-refresh" 
                  size="small"
                  severity="info"
                  onClick={handleDesignQuestionnaire} 
                  loading={processing || isPolling}
                  tooltip="Re-run questionnaire design with workflow"
                />
              </div>
              <DataTable value={project.questionnaire.sections} className="p-datatable-sm">
                <Column field="title" header="Section" />
                <Column field="questions" header="Questions" body={(row: any) => row.questions.length} />
              </DataTable>
            </div>
          ) : (
            <span style={{ color: '#9ca3af' }}>Complete the indicator extraction step first</span>
          )}
        </div>

        {/* Step 4: Deploy to Kobo */}
        <div style={cardStyle}>
          <h3 style={{ fontSize: '1.125rem', margin: '0 0 0.5rem 0', color: '#1f2937' }}>4. Deploy to KoboToolbox</h3>
          <p style={{ color: '#6b7280', marginBottom: '1rem' }}>Convert to XLSForm and deploy for data collection</p>
          
          {koboContext && !koboContext.account_active && (
            <div style={{ padding: '0.75rem', border: '1px solid #ef4444', borderRadius: '6px', backgroundColor: '#fef2f2', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <i className="pi pi-exclamation-triangle" style={{ color: '#ef4444' }}></i>
                <span style={{ fontSize: '0.875rem', color: '#ef4444', fontWeight: 500 }}>
                  KoboToolbox account is inactive
                </span>
              </div>
              <p style={{ fontSize: '0.75rem', color: '#dc2626', margin: '0.25rem 0 0 0' }}>
                Please check your KoboToolbox account settings or select a different account.
              </p>
            </div>
          )}
          
          {project.status === 'questionnaire_designed' ? (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <Button 
                label="Deploy to KoboToolbox" 
                icon="pi pi-send" 
                onClick={handleDeployToKobo} 
                loading={processing || isPolling}
                disabled={koboContext ? !koboContext.account_active : false}
              />
            </div>
          ) : project.koboFormId ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <Tag value="Deployed" severity="success" />
                <span style={{ color: '#6b7280' }}>Survey is live on KoboToolbox</span>
                {/* Allow re-deployment for existing projects */}
                <Button 
                  label="Re-deploy" 
                  icon="pi pi-refresh" 
                  size="small"
                  severity="info"
                  onClick={handleDeployToKobo} 
                  loading={processing || isPolling}
                  disabled={koboContext ? !koboContext.account_active : false}
                  tooltip="Re-deploy to KoboToolbox with workflow"
                />
              </div>
              {koboContext && (
                <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#f3f4f6', borderRadius: '6px' }}>
                  <div style={{ fontSize: '0.875rem', color: '#374151', marginBottom: '0.25rem' }}>
                    <strong>Account:</strong> {koboContext.account_name}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#374151', marginBottom: '0.25rem' }}>
                    <strong>Form ID:</strong> {koboContext.form_id}
                  </div>
                  {koboContext.last_sync && (
                    <div style={{ fontSize: '0.875rem', color: '#374151' }}>
                      <strong>Last Updated:</strong> {new Date(koboContext.last_sync).toLocaleString()}
                    </div>
                  )}
                </div>
              )}
              {project.koboFormUrl && (
                <Button label="Open in KoboToolbox" icon="pi pi-external-link" text onClick={() => window.open(project.koboFormUrl, '_blank')} />
              )}
            </div>
          ) : (
            <span style={{ color: '#9ca3af' }}>Complete the questionnaire design step first</span>
          )}
        </div>
      </div>

      {/* Workflow History Section */}
      <div style={{ padding: '2rem', backgroundColor: 'white' }}>
        <WorkflowHistory
          projectId={projectId || ''}
          currentWorkflow={workflow}
          onRefreshWorkflow={refreshWorkflow}
          workflowLoading={workflowLoading}
          onPauseWorkflow={pauseWorkflow}
          onResumeWorkflow={resumeWorkflow}
          onCancelWorkflow={cancelWorkflow}
          onRetryWorkflow={retryWorkflow}
          onContactSupport={handleContactSupport}
          onWorkflowSelect={(executionArn) => {
            setCurrentWorkflowArn(executionArn);
            startPolling(executionArn);
          }}
        />
      </div>

      {/* TOR Builder Dialog */}
      <Dialog
        header="🤖 TOR Builder Assistant"
        visible={showTORBuilderDialog}
        onHide={() => setShowTORBuilderDialog(false)}
        style={{ width: '70vw', minWidth: '600px', maxWidth: '900px' }}
        modal
        maximizable
      >
        <div style={{ minHeight: '400px', maxHeight: '600px', overflowY: 'auto' }}>
          {/* Progress Bar */}
          {(torBuilderProgress.completion_percentage !== undefined || Object.keys(torBuilderProgress).length > 0) && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                  Progress: {torBuilderProgress.completion_percentage || 0}%
                </span>
                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  Section {torBuilderProgress.sections_completed || 0} of 10
                </span>
              </div>
              <ProgressBar 
                value={torBuilderProgress.completion_percentage || 0} 
                style={{ height: '6px' }}
                color="#3B82F6"
              />
              {/* Debug info - remove in production */}
              <div style={{ fontSize: '0.7rem', color: '#999', marginTop: '0.25rem' }}>
                Debug: {JSON.stringify(torBuilderProgress)}
              </div>
            </div>
          )}

          {/* Agent Response */}
          <div style={{
            padding: '1rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            marginBottom: '1rem',
            whiteSpace: 'pre-wrap',
            lineHeight: 1.6
          }}>
            {torBuilderResponse || 'Starting TOR Builder...'}
          </div>

          {/* User Input */}
          {!torBuilderCompleted && (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                placeholder={torBuilderLoading ? "Processing..." : "Type your response here..."}
                disabled={torBuilderLoading}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  opacity: torBuilderLoading ? 0.6 : 1
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !torBuilderLoading) {
                    const input = e.target as HTMLInputElement;
                    if (input.value.trim()) {
                      handleTORBuilderMessage(input.value);
                      input.value = '';
                    }
                  }
                }}
              />
              <Button
                label="Send"
                icon={torBuilderLoading ? "pi pi-spin pi-spinner" : "pi pi-send"}
                loading={torBuilderLoading}
                disabled={torBuilderLoading}
                onClick={() => {
                  if (!torBuilderLoading) {
                    const input = document.querySelector('input[placeholder*="Type your response"]') as HTMLInputElement;
                    if (input && input.value.trim()) {
                      handleTORBuilderMessage(input.value);
                      input.value = '';
                    }
                  }
                }}
                size="small"
              />
            </div>
          )}

          {/* Completion Actions */}
          {torBuilderCompleted && (
            <div style={{
              padding: '1.5rem',
              backgroundColor: '#f0f9ff',
              border: '2px solid #3b82f6',
              borderRadius: '8px',
              marginTop: '1rem'
            }}>
              <h4 style={{ margin: '0 0 1rem 0', color: '#1e40af', fontSize: '1.125rem' }}>
                🎉 Your TOR is Complete!
              </h4>
              
              {/* TOR Content Display */}
              <div style={{
                backgroundColor: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                padding: '1rem',
                marginBottom: '1rem',
                maxHeight: '300px',
                overflowY: 'auto',
                fontSize: '0.875rem',
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace'
              }}>
                {(() => {
                  // Extract and clean TOR content from the response
                  const torMatch = torBuilderResponse.match(/TERMS OF REFERENCE \(TOR\)[\s\S]*?(?=---|\n\n\*\*Next Steps)/);
                  if (torMatch) {
                    return torMatch[0].trim();
                  }
                  return 'TOR content not found. Please try generating again.';
                })()}
              </div>
              
              <p style={{ fontSize: '0.875rem', color: '#1e40af', margin: '0 0 1rem 0' }}>
                Please review your TOR document above. You can copy it to save as a file, make modifications, or start over if needed.
              </p>
              
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <Button
                  label="🚀 Save TOR & Start Workflow"
                  icon="pi pi-play"
                  size="small"
                  severity="success"
                  onClick={async () => {
                    try {
                      setProcessing(true);
                      
                      // Extract TOR content
                      const torMatch = torBuilderResponse.match(/TERMS OF REFERENCE \(TOR\)[\s\S]*?(?=---|\n\n\*\*Next Steps)/);
                      if (!torMatch) {
                        throw new Error('Could not extract TOR content');
                      }
                      
                      const torContent = torMatch[0].trim();
                      
                      // Save TOR and get ready status
                      const saveResponse = await apiService.saveTORAndStart({
                        tor_content: torContent,
                        project_id: projectId!
                      });
                      
                      if (!saveResponse.success) {
                        throw new Error(saveResponse.error || 'Failed to save TOR');
                      }
                      
                      // Wait a moment for the save to be processed
                      await new Promise(resolve => setTimeout(resolve, 1000));
                      
                      // Reload project to get updated status
                      await loadProject();
                      
                      // Start the full workflow
                      const workflowResponse = await apiService.startFullWorkflow(projectId!, {
                        questionnaire_config: {
                          title: `${project?.title} Survey`,
                          description: project?.description,
                          include_demographics: true,
                          language: 'en'
                        },
                        deployment_config: {
                          deploy_immediately: true
                        }
                      });
                      
                      if (workflowResponse.success && workflowResponse.data) {
                        setCurrentWorkflowArn(workflowResponse.data.execution_arn);
                        
                        // Add background process
                        addProcess({
                          id: workflowResponse.data.execution_arn,
                          title: `${project?.title} Workflow`,
                          description: 'Starting full research workflow...',
                          status: 'running',
                          progress: 0,
                          canCancel: true,
                          canPause: true,
                          onCancel: async () => {
                            await cancelWorkflow();
                          },
                          onPause: async () => {
                            await pauseWorkflow();
                          },
                          onResume: async () => {
                            await resumeWorkflow();
                          }
                        });
                        
                        startPolling(workflowResponse.data.execution_arn);
                        
                        toast.current?.show({
                          severity: 'success',
                          summary: 'TOR Saved & Workflow Started!',
                          detail: 'Your TOR has been automatically uploaded and the full research workflow is now running.',
                          life: 8000
                        });
                        
                        // Close TOR Builder dialog
                        setShowTORBuilderDialog(false);
                      } else {
                        throw new Error(workflowResponse.error || 'Failed to start workflow');
                      }
                      
                    } catch (error) {
                      const errorMessage = error instanceof Error ? error.message : 'Failed to save TOR and start workflow';
                      toast.current?.show({
                        severity: 'error',
                        summary: 'Workflow Start Failed',
                        detail: errorMessage,
                        life: 8000
                      });
                    } finally {
                      setProcessing(false);
                    }
                  }}
                  loading={processing}
                  disabled={processing}
                />
                
                <Button
                  label="💾 Save TOR Only"
                  icon="pi pi-save"
                  size="small"
                  severity="info"
                  onClick={async () => {
                    try {
                      setProcessing(true);
                      
                      // Extract TOR content
                      const torMatch = torBuilderResponse.match(/TERMS OF REFERENCE \(TOR\)[\s\S]*?(?=---|\n\n\*\*Next Steps)/);
                      if (!torMatch) {
                        throw new Error('Could not extract TOR content');
                      }
                      
                      const torContent = torMatch[0].trim();
                      
                      // Save TOR only
                      const saveResponse = await apiService.saveTORAndStart({
                        tor_content: torContent,
                        project_id: projectId!
                      });
                      
                      if (!saveResponse.success) {
                        throw new Error(saveResponse.error || 'Failed to save TOR');
                      }
                      
                      // Reload project to get updated status
                      await loadProject();
                      
                      toast.current?.show({
                        severity: 'success',
                        summary: 'TOR Saved Successfully',
                        detail: 'Your TOR has been saved to the project. You can now start the workflow manually.',
                        life: 8000
                      });
                      
                      // Close TOR Builder dialog
                      setShowTORBuilderDialog(false);
                      
                    } catch (error) {
                      const errorMessage = error instanceof Error ? error.message : 'Failed to save TOR';
                      toast.current?.show({
                        severity: 'error',
                        summary: 'TOR Save Failed',
                        detail: errorMessage,
                        life: 8000
                      });
                    } finally {
                      setProcessing(false);
                    }
                  }}
                  loading={processing}
                  disabled={processing}
                />
                
                <Button
                  label="📋 Copy TOR to Clipboard"
                  icon="pi pi-copy"
                  size="small"
                  text
                  onClick={() => {
                    // Extract TOR content from the response
                    const torMatch = torBuilderResponse.match(/TERMS OF REFERENCE \(TOR\)[\s\S]*?(?=---|\n\n\*\*Next Steps)/);
                    if (torMatch) {
                      const torContent = torMatch[0].trim();
                      navigator.clipboard.writeText(torContent).then(() => {
                        toast.current?.show({
                          severity: 'success',
                          summary: 'TOR Copied Successfully',
                          detail: 'Your TOR has been copied to clipboard. You can now paste it into a document editor (Word, Google Docs, etc.) and save as a file for upload.',
                          life: 10000
                        });
                      }).catch(() => {
                        // Fallback: Create a downloadable text file
                        const blob = new Blob([torContent], { type: 'text/plain' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `${project?.title || 'Project'}_TOR.txt`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                        
                        toast.current?.show({
                          severity: 'info',
                          summary: 'TOR Downloaded',
                          detail: 'Could not copy to clipboard, but your TOR has been downloaded as a text file.',
                          life: 8000
                        });
                      });
                    } else {
                      toast.current?.show({
                        severity: 'error',
                        summary: 'TOR Content Not Found',
                        detail: 'Could not extract TOR content. Please try generating again.',
                        life: 5000
                      });
                    }
                  }}
                />
                
                <Button
                  label="📝 Modify Section"
                  icon="pi pi-pencil"
                  size="small"
                  severity="info"
                  onClick={() => {
                    // Allow user to go back and modify sections
                    setTorBuilderCompleted(false);
                    setTorBuilderProgress({
                      current_section: 'expected_impact',
                      completion_percentage: 90,
                      sections_completed: 9
                    });
                    setTorBuilderResponse(`🔄 **Modify Your TOR**

You can now modify any section of your TOR. Which section would you like to update?

**Available sections:**
1. Project Title & Focus
2. Background & Context
3. Research Objectives
4. Research Questions
5. Target Population
6. Methodology
7. Expected Outcomes
8. Key Indicators
9. Timeline
10. Expected Impact

Just tell me which section you'd like to modify, or type "continue" to proceed with the current TOR.`);
                  }}
                />
                
                <Button
                  label="🔄 Start Over"
                  icon="pi pi-refresh"
                  size="small"
                  severity="warning"
                  text
                  onClick={() => {
                    setTorBuilderCompleted(false);
                    setTorBuilderProgress({});
                    handleOpenTORBuilder();
                  }}
                />
                
                <Button
                  label="✅ Accept & Close"
                  icon="pi pi-check"
                  size="small"
                  severity="success"
                  onClick={() => {
                    toast.current?.show({
                      severity: 'success',
                      summary: 'TOR Accepted',
                      detail: 'Your TOR is ready! Save the copied content as a document file and upload it to your project.',
                      life: 8000
                    });
                    setShowTORBuilderDialog(false);
                  }}
                />
              </div>
              
              <div style={{
                marginTop: '1rem',
                padding: '0.75rem',
                backgroundColor: '#fef3c7',
                border: '1px solid #f59e0b',
                borderRadius: '4px',
                fontSize: '0.75rem',
                color: '#92400e'
              }}>
                <strong>💡 Next Steps:</strong>
                <ol style={{ margin: '0.25rem 0 0 0', paddingLeft: '1rem' }}>
                  <li>Copy the TOR content above</li>
                  <li>Paste into a document editor (Word, Google Docs, etc.)</li>
                  <li>Save as PDF, Word document, or text file</li>
                  <li>Upload the file to your research project</li>
                </ol>
              </div>
            </div>
          )}
        </div>

        <div style={{ textAlign: 'right', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
          <Button
            label="Close"
            onClick={() => setShowTORBuilderDialog(false)}
            text
          />
        </div>
      </Dialog>

      {/* Workflow Status Dialog */}
      <Toast ref={toast} />
    </div>
  );
};

export default ProjectWorkflowPage;
